import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import {
  Box,
  Card,
  Chip,
  IconButton,
  Typography,
} from "@mui/material";
import { memo, useCallback } from "react";
import { IReport } from "../../../models/report";
import { ReportDescriptionButton } from "./ReportDescriptionButton";
import { ReportEditDescriptionButton } from "./ReportEditDescriptionButton";
import { getReportCode } from "./shared";

export interface IReportCardProps {
  report: IReport;
  onClick?: (report: IReport) => void;
  onDelete?: (report: IReport) => void;
  canEditDescription?: boolean;
  onDescriptionSaved?: () => void;
}

const ReportCardComponent: React.FC<IReportCardProps> = ({
  report,
  onClick,
  onDelete,
  canEditDescription = false,
  onDescriptionSaved,
}) => {
  const name = report.name ?? "-";
  const reportCode = getReportCode(report);
  const description = report.description?.trim();

  const handleCardClick = useCallback(() => {
    onClick?.(report);
  }, [onClick, report]);

  const handleDeleteClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      onDelete?.(report);
    },
    [onDelete, report],
  );

  return (
    <Card
      elevation={0}
      onClick={handleCardClick}
      sx={(theme) => ({
        p: 2.5,
        height: "100%",
        minHeight: 120,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        transition: "all 0.2s ease-in-out",
        backgroundColor:
          theme.palette.mode === "dark"
            ? theme.palette.grey[900]
            : "transparent",
        cursor: onClick ? "pointer" : "default",
        "&:hover": {
          border: `1px solid ${theme.palette.primary.main}`,
          boxShadow: "0px 0px 20px -12px rgba(0,0,0,0.5)",
        },
      })}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
          <DescriptionRoundedIcon sx={{ fontSize: 28, mt: 0.25 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontWeight: 500,
                lineHeight: 1.3,
                wordBreak: "break-word",
              }}
            >
              {name}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", flexShrink: 0 }}>
          {canEditDescription && onDescriptionSaved && (
            <ReportEditDescriptionButton
              report={report}
              onSaved={onDescriptionSaved}
            />
          )}

          {description && <ReportDescriptionButton description={description} />}

          {onDelete && (
            <IconButton
              size="small"
              onClick={handleDeleteClick}
              aria-label="delete report"
            >
              <DeleteOutlineRoundedIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: 1.5,
        }}
      >
        {reportCode !== undefined && reportCode !== null && (
          <Chip size="small" label={`#${reportCode}`} />
        )}
      </Box>
    </Card>
  );
};

export const ReportCard = memo(ReportCardComponent);
